import { type SubmitHandler, useForm } from 'react-hook-form';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    FieldError,
    Fieldset,
    Form,
    Label,
    Submit,
    TextField,
    TextInput,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient } from '../../../state';
import { DraftBadge } from './DraftBadge';
import { SpaceGitSyncSiteOverrideAlert } from './SpaceGitSyncSiteOverrideAlert';

type VariantFormData = {
    title: string;
};

export interface CreateSiteVariantFormProps {
    organization: api.Organization;
    site: api.Site;
    siteSection: api.SiteSection | null;
    /** Existing space to link, or `null` to create and link a space on submission. */
    space: api.Space | null;
    /** Template for the new space when `space` is `null`. */
    template?: api.SpaceTemplate | null;
    pageId?: string | null;
    defaultTitle?: string;
    onCreateVariant?: (siteSpace: api.SiteSpace, siteSection: api.SiteSection | null) => void;
}

export function getCreateSiteVariantRequest(input: {
    draft: boolean;
    spaceId: string | null;
    sectionId?: string;
    pageId: string | null;
    title: string;
    template?: api.ApplySpaceTemplate;
}) {
    return {
        draft: input.draft,
        spaceId: input.spaceId,
        sectionId: input.sectionId,
        title: input.title,
        ...(input.template ? { template: input.template } : {}),
        ...(input.pageId ? { pageId: input.pageId } : {}),
    };
}

export function CreateSiteVariantForm(props: CreateSiteVariantFormProps) {
    const {
        organization,
        site,
        siteSection,
        space,
        template,
        pageId = null,
        defaultTitle,
        onCreateVariant,
    } = props;
    const form = useForm<VariantFormData>({
        defaultValues: {
            title: defaultTitle ?? space?.title ?? template?.title ?? 'Untitled',
        },
    });
    const [isPending, startDialogAction] = useDialogAction();

    const onSubmit: SubmitHandler<VariantFormData> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const { data: siteSpace } = await apiClient.siteSpaces.addSpaceToSite(
                    organization.id,
                    site.id,
                    getCreateSiteVariantRequest({
                        draft: true,
                        spaceId: space?.id ?? null,
                        sectionId: siteSection?.id,
                        pageId,
                        title: data.title,
                        template: template ? { id: template.id } : undefined,
                    }),
                    { returnPreference: 'representation' }
                );
                if (!space) {
                    analytics.logEvent(
                        analytics.createSpaceEvent(siteSpace.space, {
                            type: 'space_create',
                            source: template ? 'template' : 'blank',
                        })
                    );
                }
                if (template) {
                    analytics.logEvent({
                        type: 'templates_pick_template',
                        template: template.id,
                        organization: organization.id,
                    });
                }
                onCreateVariant?.(siteSpace, siteSection);
            }, 'Error while adding site variant');
            close();
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody className="space-y-6">
                <Fieldset>
                    <TextField
                        form={form}
                        name="title"
                        required
                        minLength={api.SITE_TITLE_MIN_LENGTH}
                        maxLength={api.SITE_TITLE_MAX_LENGTH}
                    >
                        <Label>Title</Label>
                        <Description>
                            The title that will appear in the variants dropdown menu.
                        </Description>
                        <TextInput disabled={isPending} suffix={<DraftBadge />} autoFocus />
                        <div className="text-ui-small text-muted">
                            <Icon.InfoCircle className="-mt-0.5 mr-1.5 inline-block shrink-0" />
                            Variants start as drafts and aren't visible on your published site until
                            undrafted.
                        </div>
                        <FieldError />
                    </TextField>
                </Fieldset>

                {space ? (
                    <SpaceGitSyncSiteOverrideAlert space={space} site={site} className="mt-6" />
                ) : null}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Add variant" disabled={isPending} />
            </DialogFooter>
        </Form>
    );
}
